// helpdesk-frontend/src/components/OknoWygladu/OknoWygladu.js
/**
 * [polecenie właściciela 2026-10-03] OKNO INFORMACYJNE „WYGLĄD APLIKACJI”.
 * „ustaw, że kolor domyślny aplikacji to biały, nie ciemny, user to potem sobie zmieni, ale fajnie też dać mu
 * jakiś modal, że może to sobie zmieniać itp, taki modal informacyjny”.
 *
 * Co mówi: aplikacja ma domyślnie jasny wygląd, możesz go zmienić w każdej chwili i GDZIE dokładnie.
 * Co pozwala: wybrać Jasny / Ciemny od razu w oknie (podgląd na żywo, zapis jako jawny wybór na koncie).
 *
 * KIEDY SIĘ POKAZUJE (`OknoWygladuStartowe`, wpięte w Layout):
 *  - raz na KONTO — znacznik na serwerze (PersonalizationContext → POST /api/user-preferences/okno-wygladu),
 *    lustro w przeglądarce na wypadek, gdy zapis nie dojdzie;
 *  - po zalogowaniu, w powłoce aplikacji — nigdy na logowaniu ani na stronach publicznych (te stoją poza Layoutem);
 *  - PO oknach obowiązkowych: regulamin, formularz konta, komunikat. Dopóki powłoka ich nie sprawdziła albo
 *    któreś jest otwarte, okna wyglądu nie ma. Bramka, która przyjdzie później (SignalR), chowa je bez odhaczania;
 *  - nie w podglądzie cudzego konta, nie na Sygnaliście, w trakcie nauki/testu e-learningu ani przy wypełnianiu
 *    ankiety, i nie na wierzchu innego otwartego okna (podgląd dokumentu, szuflada, karta zgłoszenia z linku)
 *    ani obok kafelka cookies czekającego na decyzję (domknięcie po krytyku 2026-10-04);
 *  - gdy preferencji konta nie udało się wczytać — nie teraz (fail-closed), spróbuje przy następnym wejściu.
 * Zamknięcie: ✕, Escape, „Rozumiem” albo „Przejdź do ustawień wyglądu”. Klik w tło NIE zamyka (W-10, potrząśnięcie).
 * Ręcznie: menu pod imieniem i nazwiskiem w nagłówku → „Wygląd aplikacji” (zdarzenie `otworz-okno-wygladu`).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCheck, FiEye, FiMoon, FiSun, FiUser } from "react-icons/fi";
import Modal from "../Modal/Modal";
import { useTheme } from "../../context/ThemeContext";
import { usePersonalization } from "../../context/PersonalizationContext";
import { ROUTE_MAP } from "../../constants/routes";
import styles from "./OknoWygladu.module.css";

/** Ręczne otwarcie okna (menu pod imieniem i nazwiskiem w nagłówku). */
export const ZDARZENIE_OTWORZ_OKNO_WYGLADU = "otworz-okno-wygladu";
/** „Przejdź do ustawień wyglądu” — nagłówek rozwija listę „Dostępność i personalizacja”. */
export const ZDARZENIE_OTWORZ_USTAWIENIA_WYGLADU = "otworz-ustawienia-wygladu";

/** Ile czekamy po spełnieniu warunków — strona z linku (karta zgłoszenia, podgląd) zdąży otworzyć swoje okno. */
export const OPOZNIENIE_OKNA_WYGLADU_MS = 700;

/* Trasy, na których okno startowe się nie pokazuje: kanał zgłoszeń sygnalisty (osoba przyszła coś zgłosić)
   i ekrany, na których człowiek jest w trakcie zadania (nauka i test e-learningu, wypełnianie ankiety). */
const TRASY_BEZ_OKNA = ["sygnalista", "eLearningLearn", "eLearningQuiz", "surveyFill"];
const WZORCE_BEZ_OKNA = TRASY_BEZ_OKNA.flatMap((klucz) => {
    const trasa = ROUTE_MAP[klucz] || {};
    return [trasa.pl, trasa.en].filter(Boolean).map((wzorzec) =>
        new RegExp("^" + wzorzec.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/:[^/]+/g, "[^/]+") + "(/|$)"));
});

export const czyTrasaBezOknaWygladu = (sciezka) =>
    typeof sciezka === "string" && WZORCE_BEZ_OKNA.some((wzorzec) => wzorzec.test(sciezka));

const MOTYWY_W_OKNIE = [
    { id: "light", nazwa: "Jasny", opis: "Białe karty na jasnym tle. Tak aplikacja startuje.", Ikona: FiSun, domyslny: true },
    { id: "dark", nazwa: "Ciemny", opis: "Grafitowe tło i jasny tekst — mniej światła wieczorem.", Ikona: FiMoon, domyslny: false },
];

/**
 * [poprawka po przeglądzie 2026-10-04] NAPISY, KTÓRE OKNO CYTUJE Z NAGŁÓWKA. Okno mówi „kliknij X → Y”, więc X i Y
 * muszą być DOKŁADNIE tym, co człowiek zobaczy na ekranie. BYŁO ŹLE: okno kazało szukać „Dostępność i personalizacja”
 * (to tylko podpowiedź po najechaniu na ikonę oka — w liście stoją nagłówki „Dostępność” i „Personalizacja”) i księżyca
 * „w prawym górnym rogu” (na prawo od niego są jeszcze oko, dzwonek i karta z nazwiskiem). Zapadka naglowekMotyw.1003
 * sprawdza, że każdy z tych napisów naprawdę stoi w nagłówku.
 */
export const NAPISY_Z_NAGLOWKA = {
    sekcjaDostepnosci: "Dostępność",
    sekcjaPersonalizacji: "Personalizacja",
    pozycjaMotywu: "Motyw",
    pozycjaMenuKonta: "Wygląd aplikacji",
};

/**
 * [domknięcie po krytyku 2026-10-04] PRZEŁĄCZNIK W NAGŁÓWKU MA DWIE TWARZE: w jasnym wyglądzie księżyc, w ciemnym słońce
 * (Header.js: `theme === "light" ? <FiMoon/> : <FiSun/>`). BYŁO ŹLE: okno otwarte w ciemnym wyglądzie kazało szukać
 * „ikony oka tuż obok księżyca”, a księżyca wtedy na ekranie nie ma. Teraz obie ścieżki mówią o tym, co stoi na pasku.
 */
export const PRZELACZNIK_W_NAGLOWKU = {
    light: { zIkona: "z księżycem", obok: "księżyca" },
    dark: { zIkona: "ze słońcem", obok: "słońca" },
};

/** Samo okno. `isOpen` decyduje o widoczności; wybór motywu działa od razu (ThemeContext). */
const OknoWygladu = ({ isOpen, onClose, onPrzejdzDoUstawien }) => {
    const { theme, ustawMotyw, toggleTheme } = useTheme() || {};
    const biezacy = theme === "dark" ? "dark" : "light";
    const przelacznik = PRZELACZNIK_W_NAGLOWKU[biezacy];
    const rozumiemRef = useRef(null);
    const kafelkiRef = useRef({});

    const wybierz = (id) => {
        if (typeof ustawMotyw === "function") ustawMotyw(id);
        else if (id !== biezacy && typeof toggleTheme === "function") toggleTheme();
    };

    /* [poprawka po przeglądzie 2026-10-04] FOKUS NA „Rozumiem”. Okno otwiera się samo po wejściu, a wspólne okno
       stawia fokus na pierwszym przycisku treści — czyli na kafelku „Jasny”. Enter albo spacja (ktoś akurat pisał
       albo chciał zatwierdzić okno) zapisywały wtedy na koncie jawny wybór zamiast zamknąć okno. Ten efekt rodzica
       działa PO efekcie okna, więc wygrywa. */
    useEffect(() => {
        if (isOpen && rozumiemRef.current) rozumiemRef.current.focus();
    }, [isOpen]);

    /* Kafelki to grupa wyboru jednego z dwóch (radio): strzałki przechodzą i wybierają, Tab wchodzi na zaznaczony. */
    const naKlawiszKafelka = (e, indeks) => {
        const krok = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!krok) return;
        e.preventDefault();
        const nastepny = MOTYWY_W_OKNIE[(indeks + krok + MOTYWY_W_OKNIE.length) % MOTYWY_W_OKNIE.length];
        wybierz(nastepny.id);
        const el = kafelkiRef.current[nastepny.id];
        if (el && typeof el.focus === "function") el.focus();
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen onClose={onClose} title="Wygląd aplikacji" width="560px">
            <div className={styles.okno} data-testid="okno-wygladu">
                <p className={styles.wstep}>
                    Aplikacja ma teraz domyślnie <strong>jasny</strong> wygląd. Możesz go zmienić w każdej chwili —
                    to Twój wybór. Zapamiętamy go na Twoim koncie, więc zobaczysz go także na innym komputerze i telefonie.
                </p>

                <div className={styles.kafelki} role="radiogroup" aria-label="Wybierz wygląd aplikacji">
                    {MOTYWY_W_OKNIE.map(({ id, nazwa, opis, Ikona, domyslny }, indeks) => {
                        const wybrany = biezacy === id;
                        return (
                            <button
                                key={id}
                                ref={(el) => { kafelkiRef.current[id] = el; }}
                                type="button"
                                role="radio"
                                aria-checked={wybrany}
                                tabIndex={wybrany ? 0 : -1}
                                className={`${styles.kafelek} ${wybrany ? styles.kafelekWybrany : ""}`}
                                data-motyw={id}
                                onClick={() => wybierz(id)}
                                onKeyDown={(e) => naKlawiszKafelka(e, indeks)}
                            >
                                <span className={`${styles.podglad} ${id === "dark" ? styles.podgladCiemny : styles.podgladJasny}`} aria-hidden="true">
                                    <span className={styles.podgladBok} />
                                    <span className={styles.podgladTresc}>
                                        <span className={styles.podgladPasek} />
                                        <span className={styles.podgladKarta} />
                                        <span className={styles.podgladKarta} />
                                    </span>
                                </span>
                                <span className={styles.kafelekOpis}>
                                    <span className={styles.kafelekNazwa}>
                                        <Ikona aria-hidden="true" />
                                        {nazwa}
                                        {domyslny && <span className={styles.plakietka}>domyślny</span>}
                                        {/* Znacznik w wierszu nazwy, nie na miniaturze — tam zasłaniał pasek akcentu. */}
                                        {wybrany && <span className={styles.znacznik} aria-hidden="true" data-znacznik-wyboru=""><FiCheck /></span>}
                                    </span>
                                    <span className={styles.kafelekTekst}>{opis}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                <h3 className={styles.sekcjaTytul}>Gdzie zmienisz to później</h3>
                <ul className={styles.sciezki}>
                    <li>
                        <span className={styles.ikonaSciezki} aria-hidden="true">{biezacy === "dark" ? <FiSun /> : <FiMoon />}</span>
                        <span>
                            Na górnym pasku, z prawej strony: przycisk {przelacznik.zIkona} przełącza jasny i ciemny wygląd
                            jednym kliknięciem.
                        </span>
                    </li>
                    <li>
                        <span className={styles.ikonaSciezki} aria-hidden="true"><FiEye /></span>
                        <span>
                            Ikona oka tuż obok {przelacznik.obok} → sekcja <strong>{NAPISY_Z_NAGLOWKA.sekcjaPersonalizacji}</strong>
                            {" → "}<strong>{NAPISY_Z_NAGLOWKA.pozycjaMotywu}</strong>. W tej samej liście ustawisz kolor akcentu
                            i czcionkę, a w sekcji <strong>{NAPISY_Z_NAGLOWKA.sekcjaDostepnosci}</strong> — rozmiar tekstu
                            i wysoki kontrast.
                        </span>
                    </li>
                    <li>
                        <span className={styles.ikonaSciezki} aria-hidden="true"><FiUser /></span>
                        <span>
                            To okno otworzysz ponownie: kliknij swoje zdjęcie albo imię i nazwisko na górnym pasku, z prawej
                            strony → <strong>{NAPISY_Z_NAGLOWKA.pozycjaMenuKonta}</strong>.
                        </span>
                    </li>
                </ul>

                <div className={styles.stopka}>
                    <button type="button" className={styles.ghostBtn} onClick={onPrzejdzDoUstawien}>
                        Przejdź do ustawień wyglądu
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={onClose} ref={rozumiemRef}>
                        Rozumiem
                    </button>
                </div>
            </div>
        </Modal>
    );
};

/* [domknięcie po krytyku 2026-10-04] KAFELEK COOKIES. Na nowym urządzeniu stał razem z oknem: kafelek leży NAD
   przyciemnieniem okien (z-index 50010 wobec 9999), a pułapka Tab okna nie pozwalała dojść do niego z klawiatury.
   Kafelek to `role="region"`, więc warunek „inne okno otwarte” go nie widział. Okno wyglądu czeka teraz na decyzję
   o cookies („W porządku!” albo ✖) — atrybut `data-baner-cookies` stoi w BanerCookies.js. */
export const SELEKTOR_BANERU_COOKIES = "[data-baner-cookies]";

const innaWarstwaOtwarta = () =>
    typeof document !== "undefined"
    && !!document.querySelector(`[role="dialog"][aria-modal="true"], ${SELEKTOR_BANERU_COOKIES}`);

/**
 * Okno startowe wpięte w powłokę. Layout podaje stan bramek; reszta warunków jest tutaj.
 * Bez kontekstu personalizacji (atrapa w testach powłoki, brak providera) okno startowe się NIE pokazuje.
 */
export const OknoWygladuStartowe = ({ zalogowany, podgladAktywny, bramkiSprawdzone, bramkaAktywna, sciezka }) => {
    const { preferencjeWczytane, oknoWygladuWidziane, oznaczOknoWygladu } = usePersonalization() || {};
    const obslugiwane = typeof oznaczOknoWygladu === "function";
    const [zatrzasniete, setZatrzasniete] = useState(false);
    const [reczne, setReczne] = useState(false);
    const trasaWylaczona = czyTrasaBezOknaWygladu(sciezka);

    const kandydat = obslugiwane
        && !!zalogowany
        && !podgladAktywny
        && bramkiSprawdzone === true
        && !bramkaAktywna
        && preferencjeWczytane === true
        && oknoWygladuWidziane === false
        && !trasaWylaczona;

    /* Decyzja „pokaż” zatrzaskuje się na sesję, żeby okno nie znikało i nie wracało przy kolejnych renderach.
       Inne otwarte okno (aria-modal) albo kafelek cookies bez decyzji odkłada ją — sprawdzamy ponownie co 2 s. */
    useEffect(() => {
        if (!kandydat || zatrzasniete) return undefined;
        const sprawdz = () => { if (!innaWarstwaOtwarta()) setZatrzasniete(true); };
        const start = setTimeout(sprawdz, OPOZNIENIE_OKNA_WYGLADU_MS);
        const ponow = setInterval(sprawdz, 2000);
        return () => { clearTimeout(start); clearInterval(ponow); };
    }, [kandydat, zatrzasniete]);

    useEffect(() => {
        const otworz = () => setReczne(true);
        window.addEventListener(ZDARZENIE_OTWORZ_OKNO_WYGLADU, otworz);
        return () => window.removeEventListener(ZDARZENIE_OTWORZ_OKNO_WYGLADU, otworz);
    }, []);

    const automatyczne = zatrzasniete && oknoWygladuWidziane === false && !bramkaAktywna && !podgladAktywny && !trasaWylaczona;
    const otwarte = reczne || automatyczne;

    const zamknij = useCallback(() => {
        setReczne(false);
        setZatrzasniete(false);
        if (obslugiwane) oznaczOknoWygladu();
    }, [obslugiwane, oznaczOknoWygladu]);

    const przejdzDoUstawien = useCallback(() => {
        zamknij();
        window.dispatchEvent(new Event(ZDARZENIE_OTWORZ_USTAWIENIA_WYGLADU));
    }, [zamknij]);

    return <OknoWygladu isOpen={otwarte} onClose={zamknij} onPrzejdzDoUstawien={przejdzDoUstawien} />;
};

export default OknoWygladu;
